import { UserType } from "@/interfaces";
import { create } from "zustand";

type AuthSnapshotType = {
  returnTo?: string;
  token: string;
  user: UserType;
};

export type AuthStoreType = {
  token: string | null;
  setToken: (token: string | null) => void;
  user: UserType | null;
  setUser: (user: UserType | null) => void;
  authSnapshot: AuthSnapshotType | null;
  setAuthSnapshot: (snapshot: AuthSnapshotType | null) => void;
  clearAuthSnapshot: () => void;
  getUser: () => UserType | null;
  /** Epoch ms when the impersonation token expires, null when not impersonating */
  impersonationExpiresAt: number | null;
  setImpersonationExpiresAt: (expiresAt: number | null) => void;
  reset: () => void;
};

function parseStoredValue<T>(key: string): T | null {
  try {
    const rawValue = localStorage.getItem(key);
    return rawValue ? (JSON.parse(rawValue) as T) : null;
  } catch {
    return null;
  }
}

const useAuthStore = create<AuthStoreType>((set, get) => ({
  // 1. Initial token from storage
  token: localStorage.getItem("token") || null,
  
  // 2. Load user from storage if available (prevents UI flash on refresh)
  user: parseStoredValue<UserType>("user"),
  authSnapshot: parseStoredValue<AuthSnapshotType>("authSnapshot"),
  impersonationExpiresAt: parseStoredValue<number>("impersonationExpiresAt"),

  setToken: (token: string | null) => {
    if (token) {
      localStorage.setItem("token", token);
    } else {
      localStorage.removeItem("token");
    }
    set({ token });
  },

  setUser: (user: UserType | null) => {
    if (user) {
      localStorage.setItem("user", JSON.stringify(user));
    } else {
      localStorage.removeItem("user");
    }
    set({ user });
  },

  setAuthSnapshot: (snapshot: AuthSnapshotType | null) => {
    if (snapshot) {
      localStorage.setItem("authSnapshot", JSON.stringify(snapshot));
    } else {
      localStorage.removeItem("authSnapshot");
    }
    set({ authSnapshot: snapshot });
  },

  clearAuthSnapshot: () => {
    localStorage.removeItem("authSnapshot");
    localStorage.removeItem("impersonationExpiresAt");
    set({ authSnapshot: null, impersonationExpiresAt: null });
  },

  getUser: () => {
    return get().user;
  },

  setImpersonationExpiresAt: (expiresAt: number | null) => {
    if (expiresAt) {
      localStorage.setItem("impersonationExpiresAt", JSON.stringify(expiresAt));
    } else {
      localStorage.removeItem("impersonationExpiresAt");
    }
    set({ impersonationExpiresAt: expiresAt });
  },

  reset: () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("authSnapshot");
    localStorage.removeItem("impersonationExpiresAt");
    set({ token: null, user: null, authSnapshot: null, impersonationExpiresAt: null });
  },
}));

export { useAuthStore };